import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CalendarPlus,
  Camera,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  Pin,
  Plus,
  StickyNote,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { CopyVinButton } from '@/components/appointments/CopyVinButton'
import { EditableTags } from '@/components/EditableTags'
import { EditableTagsReadOnly } from '@/components/EditableTagsReadOnly'
import { OdometerPhotoUploadModal } from '@/components/vehicles/OdometerPhotoUploadModal'
import { VoiceRecorder } from '@/components/voice/VoiceRecorder'
import { useCanApproveEstimate } from '@/lib/auth'
import { formatDate, formatRelative, parseLocalDate } from '@/lib/dates'
import { formatPhone, formatVehicleLine } from '@/lib/format'
import { PhoneLink } from '@/components/PhoneLink'
import type { Tag } from '@/lib/tags'
import { decodeVin, isValidVin } from '@/lib/vin'

// ─── Types ──────────────────────────────────────────────────────────

type AppointmentStatus =
  | 'scheduled'
  | 'confirmed'
  | 'arrived'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show'
  | (string & {})

type AppointmentRow = {
  id: string
  job_id: string | null
  customer_id: string | null
  vehicle_id: string | null
  bay: number | null
  starts_at: string
  ends_at: string
  status: AppointmentStatus
  notes: string | null
  created_at: string
  customer_name: string | null
  customer_phone: string | null
  vehicle_year: number | null
  vehicle_make: string | null
  vehicle_model: string | null
  vehicle_plate: string | null
  vehicle_vin: string | null
  // Denormalized latest odometer reading (worker selects vehicles.mileage,
  // which is kept in sync with vehicle_mileage_history by the PATCH
  // /vehicles/:id batch — see worker/routes/vehicles.ts). Surfaces in
  // the detail dialog next to the VIN with an "Update from photo"
  // affordance that ties the new reading to this appointment.
  vehicle_mileage: number | null
  // Persistent vehicle notes (Eric brain v2 + per-vehicle notes
  // Feature). Surfaced read-only in the day-grid tile (badge +
  // tooltip) and the detail dialog (chip). Edits happen on
  // /vehicles/:id, never here.
  vehicle_notes: string | null
  // Tag chips so Emil sees tag context (NEW_DRIVER, VIP, etc.) directly
  // from the day grid + Edit dialog without pivoting to the customer /
  // vehicle page. Server returns [] for no tags.
  customer_tag_chips: Tag[]
  vehicle_tag_chips: Tag[]
}

type CustomerListRow = {
  id: string
  name: string
  phone: string | null
  email: string | null
}

type VehicleListRow = {
  id: string
  customer_id: string | null
  year: number | null
  make: string | null
  model: string | null
  plate: string | null
  vin: string | null
}

// ─── Calendar constants ─────────────────────────────────────────────
// Each hour row is 56px tall. The visible window is configurable per
// user via the "Hours" picker (see `WorkHours` below); the defaults
// here cover the standard 8 AM - 6 PM shop day matching the mockup.
// An appointment's top is `((startHour-firstHour) * 60 + startMin) /
// 60 * 56` px; its height is `(durationMinutes / 60) * 56` px. Mockup
// reference: ~/Desktop/Autoflo/mockups/appointments-mockup.html.
const HOUR_HEIGHT = 56
const DEFAULT_FIRST_HOUR = 8
const DEFAULT_LAST_HOUR = 18 // exclusive — grid ends at 6 PM

// Calendar viewing hours — per-user preference stored in localStorage
// so a mechanic who works split shifts keeps their picked window across
// reloads without us touching the DB. Server-side persistence (a
// `shops.work_hours_*` column) is a future move once we hit "multiple
// people need different visible windows" friction.
type WorkHours = { first: number; last: number }

const HOURS_PRESETS: ReadonlyArray<{
  key: string
  first: number
  last: number
}> = [
  { key: 'standard', first: 8, last: 18 },
  { key: 'early', first: 6, last: 18 },
  { key: 'extended', first: 6, last: 20 },
  { key: 'late', first: 8, last: 22 },
  { key: 'all_day', first: 0, last: 24 },
]

const HOURS_STORAGE_KEY = 'autoflo:calendarHours'

function loadStoredHours(): WorkHours {
  if (typeof window === 'undefined') {
    return { first: DEFAULT_FIRST_HOUR, last: DEFAULT_LAST_HOUR }
  }
  try {
    const raw = window.localStorage.getItem(HOURS_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (
        typeof parsed?.first === 'number' &&
        typeof parsed?.last === 'number' &&
        Number.isInteger(parsed.first) &&
        Number.isInteger(parsed.last) &&
        parsed.first >= 0 &&
        parsed.last <= 24 &&
        parsed.first < parsed.last
      ) {
        return { first: parsed.first, last: parsed.last }
      }
    }
  } catch {
    // Safari private-mode / corrupt JSON — fall through to defaults.
  }
  return { first: DEFAULT_FIRST_HOUR, last: DEFAULT_LAST_HOUR }
}

function persistHours(h: WorkHours): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(HOURS_STORAGE_KEY, JSON.stringify(h))
  } catch {
    // Same Safari ITP / private-mode silence as the WhatsNew dialog.
  }
}

function formatHourLabel(h: number): string {
  if (h === 0 || h === 24) return '12 AM'
  if (h === 12) return '12 PM'
  if (h < 12) return `${h} AM`
  return `${h - 12} PM`
}

// Status → chip and calendar-block colors (DESIGN.md §4.9/§4.10). The
// `block` palette tints the appointment chip on the week grid — booked
// work is a system-held commitment, so every live status shares the
// saved-soft/saved-line ground; cancelled/no_show fall back to a muted
// neutral (a cancelled slot is neither saved-green nor amber). The
// `chip` palette is the status pill used in lists and detail headers —
// lifecycle enums are NEUTRAL per §4.9 (being booked is a fact, not a
// system save), so the color per-status distinction is carried by the
// label text alone.
const STATUS_STYLES: Record<
  string,
  { block: string; chip: string; label: string }
> = {
  scheduled: {
    block: 'border border-saved-line bg-saved-soft text-foreground',
    chip: 'border-border bg-muted text-muted-foreground',
    label: 'Scheduled',
  },
  confirmed: {
    block: 'border border-saved-line bg-saved-soft text-foreground',
    chip: 'border-border bg-muted text-muted-foreground',
    label: 'Confirmed',
  },
  arrived: {
    block: 'border border-saved-line bg-saved-soft text-foreground',
    chip: 'border-border bg-muted text-muted-foreground',
    label: 'Arrived',
  },
  in_progress: {
    block: 'border border-saved-line bg-saved-soft text-foreground',
    chip: 'border-border bg-muted text-muted-foreground',
    label: 'In progress',
  },
  completed: {
    block: 'border border-border bg-muted text-muted-foreground line-through',
    chip: 'border-border bg-muted text-muted-foreground',
    label: 'Completed',
  },
  cancelled: {
    block: 'border border-border bg-muted text-muted-foreground',
    chip: 'border-border bg-muted text-muted-foreground',
    label: 'Cancelled',
  },
  no_show: {
    block: 'border border-border bg-muted text-muted-foreground',
    chip: 'border-border bg-muted text-muted-foreground',
    label: 'No show',
  },
}

// ─── Date helpers ───────────────────────────────────────────────────
// All purely local-time. The week grid + day list operate on the
// browser's timezone; the worker stores TIMESTAMPTZ so the same row
// renders correctly anywhere. (Multi-shop / multi-tz support — using
// `shops.timezone` — is a v2 concern per the spec.)

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

// Monday as the first day of the week — matches the mockup (Mon-Sat).
function startOfWeek(d: Date): Date {
  const x = startOfDay(d)
  const dow = x.getDay() // 0 = Sun, 1 = Mon, …
  const offset = dow === 0 ? -6 : 1 - dow
  x.setDate(x.getDate() + offset)
  return x
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatRange(start: Date, end: Date): string {
  const sameMonth = start.getMonth() === end.getMonth()
  const startMonth = start.toLocaleDateString(undefined, { month: 'short' })
  const endMonth = end.toLocaleDateString(undefined, { month: 'short' })
  const startDay = start.getDate()
  const endDay = end.getDate()
  const year = end.getFullYear()
  if (sameMonth) return `${startMonth} ${startDay} – ${endDay}, ${year}`
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`
}

function formatTimeShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function durationMinutesBetween(startIso: string, endIso: string): number {
  const s = new Date(startIso).getTime()
  const e = new Date(endIso).getTime()
  if (!Number.isFinite(s) || !Number.isFinite(e)) return 0
  return Math.max(0, Math.round((e - s) / 60000))
}

// Combine customer + vehicle tag chips into a single de-duplicated list
// for the tile (limited to 3 to keep mobile rows tidy). Vehicle chips
// win when a tag name appears on both, since vehicle context is usually
// more actionable on the day grid.
function mergeAppointmentTagChips(row: {
  customer_tag_chips: Tag[]
  vehicle_tag_chips: Tag[]
}): Tag[] {
  const seen = new Set<string>()
  const merged: Tag[] = []
  for (const tag of [...row.vehicle_tag_chips, ...row.customer_tag_chips]) {
    if (seen.has(tag.id)) continue
    seen.add(tag.id)
    merged.push(tag)
    if (merged.length >= 3) break
  }
  return merged
}

// Round minutes to the nearest 15-minute increment — matches Google
// Calendar's snap granularity. Used during drag/resize to commit a
// clean starts_at/ends_at instead of the raw pixel position.
function snapMinutes(min: number): number {
  return Math.round(min / 15) * 15
}

// Pixels → minutes given the row-height calibration.
function pxToMinutes(px: number): number {
  return (px / HOUR_HEIGHT) * 60
}

// Convert a Date to a `<input type="datetime-local">` value string.
function toLocalInputValue(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${hh}:${mm}`
}

function defaultStartLocal(): string {
  const d = new Date()
  d.setMinutes(0, 0, 0)
  d.setHours(d.getHours() + 1)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${hh}:${mm}`
}

// `<input type="datetime-local">` returns "YYYY-MM-DDTHH:mm" with no
// timezone. Append the user's offset so the server stores the wall-clock
// time they picked.
function localToIso(local: string): string {
  if (!local) return ''
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return local
  const tz = -d.getTimezoneOffset()
  const sign = tz >= 0 ? '+' : '-'
  const pad = (n: number) => String(Math.abs(n)).padStart(2, '0')
  const hh = pad(Math.trunc(tz / 60))
  const mm = pad(tz % 60)
  return `${local}:00${sign}${hh}:${mm}`
}

// ─── Page ───────────────────────────────────────────────────────────

type View = 'day' | 'week'

export function Appointments() {
  const { t } = useTranslation('appointments')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  // Default view: week on desktop (md+), day on mobile. We only set
  // this once on mount — afterward the user's toggle wins. The
  // `<WeekGrid hidden lg:block>` / `<DayList lg:hidden>` styling means
  // the grid is hidden on mobile regardless, so the choice mostly
  // matters when the user resizes between breakpoints.
  const [view, setView] = useState<View>(() =>
    typeof window !== 'undefined' && window.innerWidth >= 1024
      ? 'week'
      : 'day',
  )

  const [searchParams, setSearchParams] = useSearchParams()

  // The anchor date the calendar is centered on. For week view we
  // resolve to the containing week; for day view it's just the day.
  // Deep-link support: `?date=YYYY-MM-DD` (paired with `?appt=`) seeds
  // the grid on the appointment's own day so the auto-opened detail
  // isn't sitting behind a calendar still parked on today. Parsed as
  // LOCAL midnight (see parseLocalDate) so the grid lands on the same
  // day the row renders in.
  const [anchor, setAnchor] = useState<Date>(
    () => startOfDay(parseLocalDate(searchParams.get('date')) ?? new Date()),
  )
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor])
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart])

  const [sheetOpen, setSheetOpen] = useState(false)
  const [prefillStart, setPrefillStart] = useState<string | null>(null)

  // Deep-link support: /appointments?appt=APPT-XYZ auto-opens the edit
  // dialog for that appointment on mount. Used by the LiveAppointment
  // tile on the Customer + Vehicle detail pages so "tap the live tile"
  // lands directly in the appointment editor. We seed selectedRowId via
  // lazy init (no setState in effect) and strip the query param in an
  // effect so closing the dialog doesn't immediately re-open it on
  // re-render.
  const [selectedRowId, setSelectedRowId] = useState<string | null>(
    () => searchParams.get('appt'),
  )

  // Visible-hour window for the week grid — picked from a preset
  // dropdown next to Day/Week. Persists per device.
  const [workHours, setWorkHours] = useState<WorkHours>(loadStoredHours)
  const updateWorkHours = (next: WorkHours) => {
    setWorkHours(next)
    persistHours(next)
  }

  useEffect(() => {
    if (!searchParams.get('appt') && !searchParams.get('date')) return
    const next = new URLSearchParams(searchParams)
    next.delete('appt')
    next.delete('date')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch a wide enough range that nav within the visible window
  // doesn't re-fire the query. Pull 14 days starting one week before
  // the anchor so swiping back also finds rows immediately.
  const fetchFrom = useMemo(
    () => addDays(weekStart, -7).toISOString(),
    [weekStart],
  )
  const fetchTo = useMemo(
    () => addDays(weekStart, 14).toISOString(),
    [weekStart],
  )

  const appointmentsQuery = useQuery({
    queryKey: ['appointments', fetchFrom, fetchTo],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const params = new URLSearchParams({ from: fetchFrom, to: fetchTo })
      const res = await fetch(`/api/appointments?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as AppointmentRow[]
    },
  })

  // Per-appointment carryover counts for the currently-anchored week.
  // One round-trip per week beats N per-tile fetches; the chip on each
  // tile reads from this map. Keyed by the week start so swiping
  // between weeks fetches fresh data without bleeding stale counts.
  const carryoverWeekStartIso = useMemo(
    () => weekStart.toISOString(),
    [weekStart],
  )
  const carryoverCountsQuery = useQuery({
    queryKey: ['carryover-counts', carryoverWeekStartIso],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(
        `/api/appointments/week/${encodeURIComponent(carryoverWeekStartIso)}/carryover-counts`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) return { counts: {} as Record<string, number> }
      return (await res.json()) as { counts: Record<string, number> }
    },
  })
  const carryoverCounts = carryoverCountsQuery.data?.counts ?? {}

  const allRows = appointmentsQuery.data ?? []
  const selectedRow = selectedRowId
    ? allRows.find((r) => r.id === selectedRowId) ?? null
    : null

  // Reschedule (drag) and resize commit through this mutation. Optimistic
  // updates patch every cached appointment list (any query key matching
  // `['appointments', ...]`) so the UI snaps to the new slot instantly;
  // we invalidate on settle to reconcile with the server.
  const rescheduleMutation = useMutation({
    mutationFn: async (vars: {
      id: string
      starts_at: string
      ends_at: string
    }) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/appointments/${vars.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          starts_at: vars.starts_at,
          ends_at: vars.ends_at,
        }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as { id: string }
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      const snapshots: { key: unknown[]; data: AppointmentRow[] | undefined }[] =
        []
      queryClient
        .getQueriesData<AppointmentRow[]>({ queryKey: ['appointments'] })
        .forEach(([key, data]) => {
          snapshots.push({ key: key as unknown[], data })
          if (!data) return
          queryClient.setQueryData(
            key,
            data.map((r) =>
              r.id === vars.id
                ? { ...r, starts_at: vars.starts_at, ends_at: vars.ends_at }
                : r,
            ),
          )
        })
      return { snapshots }
    },
    onError: (_err, _vars, ctx) => {
      // Roll back optimistic updates on failure.
      ctx?.snapshots.forEach((s) => queryClient.setQueryData(s.key, s.data))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
    },
  })

  function goPrev() {
    setAnchor((a) => (view === 'week' ? addDays(a, -7) : addDays(a, -1)))
  }
  function goNext() {
    setAnchor((a) => (view === 'week' ? addDays(a, 7) : addDays(a, 1)))
  }
  function goToday() {
    setAnchor(startOfDay(new Date()))
  }
  function openNewSheet(opts?: { start?: Date }) {
    if (opts?.start) {
      const d = opts.start
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      setPrefillStart(`${y}-${m}-${day}T${hh}:${mm}`)
    } else {
      setPrefillStart(null)
    }
    setSheetOpen(true)
  }

  const rangeLabel =
    view === 'week'
      ? formatRange(weekStart, weekEnd)
      : anchor.toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })

  return (
    <div className="space-y-4">
      {/* §4.5 page header: shop line above a balanced 22px title; the
          right slot holds the single primary action. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <header className="space-y-0.5">
          {organization?.name ? (
            <div className="text-[13px] font-medium text-muted-foreground">
              {organization.name}
            </div>
          ) : null}
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
        </header>

        <Button onClick={() => openNewSheet()} className="gap-2">
          <CalendarPlus className="size-4" />
          {t('actions.new_appointment')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* The label gets a generous min-width on lg+ so the prev/next
            row stays stable as the week range string varies; on mobile
            it claims only the room it needs so the Today button doesn't
            wrap to its own line. */}
        <div className="flex w-full items-center gap-1 sm:w-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={goPrev}
            aria-label={t('nav.previous')}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <div className="flex-1 px-2 text-center text-sm font-semibold text-foreground sm:flex-none sm:text-left sm:text-base lg:min-w-[14rem]">
            {rangeLabel}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={goNext}
            aria-label={t('nav.next')}
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToday}
            className="ml-2"
          >
            {t('nav.today')}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {/* Hours picker. Visible on Week view (lg+) where the grid is
              bounded by the hour window; hidden in the mobile day
              list where appointments render regardless of the visible
              hours band. */}
          {view === 'week' ? (
            <select
              aria-label={t('hours_label')}
              value={`${workHours.first}-${workHours.last}`}
              onChange={(e) => {
                const [first, last] = e.target.value.split('-').map(Number)
                if (Number.isFinite(first) && Number.isFinite(last)) {
                  updateWorkHours({ first, last })
                }
              }}
              className="hidden h-7 rounded-md border border-border bg-background px-2 text-xs text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 lg:inline-flex"
            >
              {HOURS_PRESETS.map((p) => (
                <option key={p.key} value={`${p.first}-${p.last}`}>
                  {t(`hours_presets.${p.key}`, {
                    defaultValue: `${formatHourLabel(p.first)} – ${formatHourLabel(p.last)}`,
                  })}
                </option>
              ))}
            </select>
          ) : null}

          {/* Day / Week toggle. Hidden on mobile — the full Week grid
              doesn't fit below lg, so below lg both views collapse to the
              same DayList (with the WeekStrip calendar above it). Showing
              the toggle there is just noise. */}
          <div className="hidden rounded-lg bg-muted p-0.5 text-xs font-medium lg:inline-flex">
            <button
              type="button"
              onClick={() => setView('day')}
              className={
                view === 'day'
                  ? 'rounded-[10px] bg-background px-3 py-1 text-foreground shadow-1'
                  : 'rounded-[10px] px-3 py-1 text-muted-foreground transition-colors [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground'
              }
            >
              {t('view.day')}
            </button>
            <button
              type="button"
              onClick={() => setView('week')}
              className={
                view === 'week'
                  ? 'rounded-[10px] bg-background px-3 py-1 text-foreground shadow-1'
                  : 'rounded-[10px] px-3 py-1 text-muted-foreground transition-colors [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground'
              }
            >
              {t('view.week')}
            </button>
          </div>
        </div>
      </div>

      {appointmentsQuery.isError ? (
        <div className="rounded-[14px] border border-destructive/30 bg-destructive-soft p-4 text-sm text-destructive">
          {appointmentsQuery.error instanceof Error
            ? appointmentsQuery.error.message
            : t('error')}
        </div>
      ) : null}

      {/* Mobile calendar overview — a horizontal week strip that's
          always visible below lg. Gives the at-a-glance "which days are
          busy" view that the desktop WeekGrid provides; tapping a pill
          sets the focused day for the DayList below. */}
      <div className="lg:hidden">
        <WeekStrip
          weekStart={weekStart}
          anchor={anchor}
          rows={allRows}
          onSelectDay={(d) => setAnchor(d)}
        />
      </div>

      {view === 'week' ? (
        <>
          <div className="hidden lg:block">
            <WeekGrid
              weekStart={weekStart}
              rows={allRows}
              firstHour={workHours.first}
              lastHour={workHours.last}
              carryoverCounts={carryoverCounts}
              onSlotClick={(date) => openNewSheet({ start: date })}
              onAppointmentClick={(id) => setSelectedRowId(id)}
              onReschedule={(vars) => rescheduleMutation.mutate(vars)}
            />
          </div>
          {/* Week grid doesn't fit on phones; mirror the day-list on
              the focused day for the no-grid view. */}
          <div className="lg:hidden">
            <DayList
              day={anchor}
              rows={allRows}
              carryoverCounts={carryoverCounts}
              onEmptyCta={() => openNewSheet({ start: anchor })}
              onAppointmentClick={(id) => setSelectedRowId(id)}
            />
          </div>
        </>
      ) : (
        <DayList
          day={anchor}
          rows={allRows}
          carryoverCounts={carryoverCounts}
          onEmptyCta={() => openNewSheet({ start: anchor })}
          onAppointmentClick={(id) => setSelectedRowId(id)}
        />
      )}

      <NewAppointmentDialog
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        prefillStartLocal={prefillStart}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['appointments'] })
          setSheetOpen(false)
        }}
      />

      <AppointmentDetailDialog
        row={selectedRow}
        open={!!selectedRow}
        onOpenChange={(open) => {
          if (!open) setSelectedRowId(null)
        }}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['appointments'] })
        }}
        onDeleted={() => {
          queryClient.invalidateQueries({ queryKey: ['appointments'] })
          setSelectedRowId(null)
        }}
      />
    </div>
  )
}

// ─── Week grid ──────────────────────────────────────────────────────

type DragMode = 'move' | 'resize'

// Live drag/resize state. The block is "lifted" once the pointer has
// traveled DRAG_THRESHOLD pixels; below that it's still a click, which
// opens the detail dialog instead. Snap granularity is 15 min (matches
// Google Calendar).
type DragState = {
  rowId: string
  mode: DragMode
  origStart: Date
  origEnd: Date
  startMouseX: number
  startMouseY: number
  dayOffset: number // signed integer day shift (move mode only)
  minuteOffset: number // signed minute shift (snapped to 15)
  isDragging: boolean
}

const DRAG_THRESHOLD_PX = 5

function WeekGrid({
  weekStart,
  rows,
  firstHour,
  lastHour,
  carryoverCounts,
  onSlotClick,
  onAppointmentClick,
  onReschedule,
}: {
  weekStart: Date
  rows: AppointmentRow[]
  firstHour: number
  lastHour: number
  carryoverCounts: Record<string, number>
  onSlotClick: (date: Date) => void
  onAppointmentClick: (id: string) => void
  onReschedule: (vars: {
    id: string
    starts_at: string
    ends_at: string
  }) => void
}) {
  // Derive from the visible-window props so the grid resizes when the
  // user changes the Hours picker. `totalHours` is the row count for
  // the time column + each day column, used in the inline styles.
  const totalHours = lastHour - firstHour
  const { t } = useTranslation('appointments')
  const gridRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<DragState | null>(null)
  // Keep a live ref alongside the state so window event handlers see
  // the latest snapshot without restarting the listeners on each
  // pointermove (state updates would otherwise cause stale closures).
  const dragRef = useRef<DragState | null>(null)

  const days = useMemo(
    () => Array.from({ length: 6 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  )

  const rowsByDay = useMemo(() => {
    const map = new Map<string, AppointmentRow[]>()
    for (const r of rows) {
      const d = new Date(r.starts_at)
      if (Number.isNaN(d.getTime())) continue
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
      const arr = map.get(key)
      if (arr) arr.push(r)
      else map.set(key, [r])
    }
    return map
  }, [rows])

  const today = new Date()

  // Tick the current-time indicator every minute so it walks down
  // today's column without a manual refresh.
  const [, setNowTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60_000)
    return () => clearInterval(id)
  }, [])

  function startDrag(opts: {
    rowId: string
    row: AppointmentRow
    mode: DragMode
    clientX: number
    clientY: number
  }) {
    const next: DragState = {
      rowId: opts.rowId,
      mode: opts.mode,
      origStart: new Date(opts.row.starts_at),
      origEnd: new Date(opts.row.ends_at),
      startMouseX: opts.clientX,
      startMouseY: opts.clientY,
      dayOffset: 0,
      minuteOffset: 0,
      isDragging: false,
    }
    dragRef.current = next
    setDrag(next)

    function onMove(ev: PointerEvent) {
      const current = dragRef.current
      if (!current) return
      const grid = gridRef.current
      if (!grid) return
      const rect = grid.getBoundingClientRect()
      const columnWidth = Math.max(1, (rect.width - 60) / 6)

      const deltaX = ev.clientX - current.startMouseX
      const deltaY = ev.clientY - current.startMouseY
      const dist = Math.hypot(deltaX, deltaY)
      const wasDragging = current.isDragging
      const isDragging = wasDragging || dist > DRAG_THRESHOLD_PX

      let dayOffset = 0
      const minuteOffset = snapMinutes(pxToMinutes(deltaY))

      if (current.mode === 'move') {
        dayOffset = Math.round(deltaX / columnWidth)
      }

      const updated: DragState = {
        ...current,
        dayOffset,
        minuteOffset,
        isDragging,
      }
      dragRef.current = updated
      setDrag(updated)
    }

    function onUp(_ev: PointerEvent) {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      const current = dragRef.current
      dragRef.current = null
      setDrag(null)
      if (!current) return

      if (!current.isDragging) {
        // Plain click — open detail dialog.
        onAppointmentClick(current.rowId)
        return
      }

      // Commit drag
      if (current.mode === 'move') {
        const newStart = new Date(current.origStart)
        newStart.setDate(newStart.getDate() + current.dayOffset)
        newStart.setMinutes(newStart.getMinutes() + current.minuteOffset)
        const newEnd = new Date(current.origEnd)
        newEnd.setDate(newEnd.getDate() + current.dayOffset)
        newEnd.setMinutes(newEnd.getMinutes() + current.minuteOffset)
        if (newStart.getTime() === current.origStart.getTime()) return
        onReschedule({
          id: current.rowId,
          starts_at: newStart.toISOString(),
          ends_at: newEnd.toISOString(),
        })
      } else {
        // Resize: only ends_at moves; enforce 15-min minimum.
        const newEnd = new Date(current.origEnd)
        newEnd.setMinutes(newEnd.getMinutes() + current.minuteOffset)
        const minEnd = new Date(current.origStart.getTime() + 15 * 60_000)
        const finalEnd = newEnd < minEnd ? minEnd : newEnd
        if (finalEnd.getTime() === current.origEnd.getTime()) return
        onReschedule({
          id: current.rowId,
          starts_at: current.origStart.toISOString(),
          ends_at: finalEnd.toISOString(),
        })
      }
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <div className="rounded-[14px] border border-border bg-card">
      {/* Header row */}
      <div
        className="grid rounded-t-[14px] border-b border-border bg-muted"
        style={{ gridTemplateColumns: '60px repeat(6, 1fr)' }}
      >
        <div />
        {days.map((d) => {
          const isToday = isSameDay(d, today)
          return (
            <div
              key={d.toISOString()}
              className={
                isToday
                  ? 'border-l border-border bg-surface-2 px-2 py-2 text-center text-xs font-semibold text-foreground'
                  : 'border-l border-border px-2 py-2 text-center text-xs font-semibold text-muted-foreground'
              }
            >
              {d.toLocaleDateString(undefined, { weekday: 'short' })}{' '}
              {d.getDate()}
            </div>
          )
        })}
      </div>

      {/* Body grid */}
      <div
        ref={gridRef}
        className="relative grid select-none"
        style={{
          gridTemplateColumns: '60px repeat(6, 1fr)',
          cursor: drag?.isDragging
            ? drag.mode === 'resize'
              ? 'ns-resize'
              : 'grabbing'
            : undefined,
        }}
      >
        {/* Time column */}
        <div className="bg-muted">
          {Array.from({ length: totalHours }, (_, i) => {
            const hour = firstHour + i
            return (
              <div
                key={hour}
                className="border-b border-border/60 px-2 py-1 text-right text-[11px] text-muted-foreground"
                style={{ height: HOUR_HEIGHT }}
              >
                {formatHourLabel(hour)}
              </div>
            )
          })}
        </div>

        {days.map((d) => (
          <DayColumn
            key={d.toISOString()}
            day={d}
            isToday={isSameDay(d, today)}
            firstHour={firstHour}
            lastHour={lastHour}
            rows={
              rowsByDay.get(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`) ??
              []
            }
            carryoverCounts={carryoverCounts}
            onSlotClick={onSlotClick}
            drag={drag}
            onStartDrag={startDrag}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/50 px-3 py-2 text-[12px]">
        {(
          [
            'scheduled',
            'confirmed',
            'arrived',
            'in_progress',
            'completed',
            'no_show',
          ] as AppointmentStatus[]
        ).map((s) => (
          <span
            key={s}
            className={`rounded-full border px-[9px] py-[3px] font-[650] ${STATUS_STYLES[s]?.chip ?? ''}`}
          >
            {t(`status.${s}`)}
          </span>
        ))}
        <span className="ml-auto text-ink-3">{t('hint.drag')}</span>
      </div>
    </div>
  )
}

function DayColumn({
  day,
  isToday,
  firstHour,
  lastHour,
  rows,
  carryoverCounts,
  onSlotClick,
  drag,
  onStartDrag,
}: {
  day: Date
  isToday: boolean
  firstHour: number
  lastHour: number
  rows: AppointmentRow[]
  carryoverCounts: Record<string, number>
  onSlotClick: (date: Date) => void
  drag: DragState | null
  onStartDrag: (opts: {
    rowId: string
    row: AppointmentRow
    mode: DragMode
    clientX: number
    clientY: number
  }) => void
}) {
  const totalHours = lastHour - firstHour

  // Current-time line. Only render on today's column AND only if "now"
  // sits inside the [firstHour, lastHour) visible window — gracefully
  // hides off-grid when the user picks a tight window like 8-6.
  const now = new Date()
  let nowTop: number | null = null
  if (isToday) {
    const m = (now.getHours() - firstHour) * 60 + now.getMinutes()
    if (m >= 0 && m < totalHours * 60) {
      nowTop = (m / 60) * HOUR_HEIGHT
    }
  }

  return (
    <div
      className={
        isToday
          ? 'relative border-l border-border bg-muted/60'
          : 'relative border-l border-border'
      }
      style={{ height: HOUR_HEIGHT * totalHours }}
    >
      {/* Hour rows (clickable to create). Each row is split visually
          into a single hour, but the click handler resolves to the
          exact hour — finer-grained start time selection happens in
          the New Appointment dialog. */}
      {Array.from({ length: totalHours }, (_, i) => {
        const hour = firstHour + i
        return (
          <button
            key={hour}
            type="button"
            className="block w-full border-b border-border/60 focus:bg-muted focus:outline-none [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
            style={{ height: HOUR_HEIGHT }}
            onClick={() => {
              const d = new Date(day)
              d.setHours(hour, 0, 0, 0)
              onSlotClick(d)
            }}
            aria-label={`Create appointment at ${hour}:00`}
          />
        )
      })}

      {/* Current-time indicator (today only). The dot pokes left of the
          column edge so it's visible even when an appointment chip sits
          at the same minute. */}
      {nowTop !== null ? (
        <div
          className="pointer-events-none absolute left-0 right-0 z-10"
          style={{ top: nowTop }}
        >
          <div className="relative h-0">
            <div className="absolute -left-1 -top-1 size-2 rounded-full bg-destructive" />
            <div className="absolute inset-x-0 -top-px h-0.5 bg-destructive" />
          </div>
        </div>
      ) : null}

      {/* Appointment chips */}
      {rows.map((r) => (
        <AppointmentBlock
          key={r.id}
          row={r}
          firstHour={firstHour}
          lastHour={lastHour}
          carryoverCount={carryoverCounts[r.id] ?? 0}
          drag={drag?.rowId === r.id ? drag : null}
          onStartDrag={onStartDrag}
        />
      ))}
    </div>
  )
}

function AppointmentBlock({
  row,
  firstHour,
  lastHour,
  carryoverCount,
  drag,
  onStartDrag,
}: {
  row: AppointmentRow
  firstHour: number
  lastHour: number
  carryoverCount: number
  drag: DragState | null
  onStartDrag: (opts: {
    rowId: string
    row: AppointmentRow
    mode: DragMode
    clientX: number
    clientY: number
  }) => void
}) {
  const { t } = useTranslation('appointments')
  const start = new Date(row.starts_at)
  const minutesFromStart =
    (start.getHours() - firstHour) * 60 + start.getMinutes()
  const top = (minutesFromStart / 60) * HOUR_HEIGHT
  const durationMin = durationMinutesBetween(row.starts_at, row.ends_at)
  const heightFromDuration = (durationMin / 60) * HOUR_HEIGHT - 2

  // Don't render blocks that start past the visible window. The Hours
  // picker (above) lets the user widen the window if a real appointment
  // is sitting in a row that's currently off-grid.
  if (start.getHours() >= lastHour || top < 0) return null

  const styles = STATUS_STYLES[row.status] ?? STATUS_STYLES.scheduled!
  const vehicleLabel = formatVehicleLine(
    row.vehicle_year,
    row.vehicle_make,
    row.vehicle_model,
  )
  const subtitle =
    vehicleLabel || row.notes || t('card.no_vehicle')

  // While the user is dragging THIS block, render it transformed by the
  // live offsets. dayOffset shifts the block horizontally by N column
  // widths (computed from the parent grid's measured size — see
  // WeekGrid's onMove). minuteOffset shifts vertically; in resize mode
  // we extend height instead of translating.
  const isDragging = drag?.isDragging === true
  let translateY = 0
  let height = Math.max(28, heightFromDuration)
  if (drag) {
    if (drag.mode === 'move') {
      // Convert dayOffset → px: we don't know the live column width
      // here, but the parent column has equal width to its siblings so
      // we can use 100% of THIS column's width as the unit. CSS-wise
      // that means we use `calc(100% * dayOffset)` — but we need the
      // value in px for the transform style, so we instead use an
      // outer wrapper that translates by `${dayOffset * 100}%` and
      // the block translates Y in px.
      translateY = (drag.minuteOffset / 60) * HOUR_HEIGHT
    } else {
      // Resize: ends_at moves; keep min 15-min duration.
      const projectedEnd = new Date(drag.origEnd)
      projectedEnd.setMinutes(projectedEnd.getMinutes() + drag.minuteOffset)
      const minEnd = new Date(drag.origStart.getTime() + 15 * 60_000)
      const finalEnd = projectedEnd < minEnd ? minEnd : projectedEnd
      const newDuration =
        (finalEnd.getTime() - drag.origStart.getTime()) / 60_000
      height = Math.max(28, (newDuration / 60) * HOUR_HEIGHT - 2)
    }
  }

  // X translation needs to be a fraction of the parent column width, so
  // express it in % and let the transform compose with translateY in px.
  const xPct = drag?.mode === 'move' ? drag.dayOffset * 100 : 0

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    onStartDrag({
      rowId: row.id,
      row,
      mode: 'move',
      clientX: e.clientX,
      clientY: e.clientY,
    })
  }

  function handleResizePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    onStartDrag({
      rowId: row.id,
      row,
      mode: 'resize',
      clientX: e.clientX,
      clientY: e.clientY,
    })
  }

  return (
    <div
      onPointerDown={handlePointerDown}
      className={`absolute left-1 right-1 select-none overflow-hidden rounded-[10px] px-2 py-1 text-[11px] leading-tight transition-shadow ${styles.block} ${
        isDragging
          ? 'cursor-grabbing shadow-2 ring-2 ring-ring/30'
          : 'cursor-grab'
      }`}
      style={{
        top,
        height,
        transform: `translate(${xPct}%, ${translateY}px)`,
        zIndex: isDragging ? 20 : 5,
      }}
      title={`${formatTimeShort(row.starts_at)} – ${formatTimeShort(row.ends_at)} · ${t('hint.click_to_edit')}`}
    >
      <div className="flex items-start gap-1">
        <GripVertical
          className="mt-0.5 size-3 shrink-0 opacity-30"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <div className="min-w-0 flex-1 truncate text-[11px] font-semibold">
              {row.customer_name ?? t('card.no_customer')}
            </div>
            {row.vehicle_notes && row.vehicle_notes.trim().length > 0 ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* The block above this is the drag handle; stop
                      pointerdown on the badge so hovering / tapping
                      it doesn't kick off a reschedule drag. */}
                  <span
                    onPointerDown={(e) => e.stopPropagation()}
                    className="inline-flex shrink-0 cursor-help items-center text-attention-text"
                    aria-label={t('tile.vehicle_notes_badge_aria')}
                  >
                    <StickyNote className="size-3" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="whitespace-pre-wrap text-xs">
                    {row.vehicle_notes}
                  </p>
                </TooltipContent>
              </Tooltip>
            ) : null}
            {row.vehicle_vin ? (
              <CopyVinButton
                vin={row.vehicle_vin}
                variant="tile"
                className="-mr-1 -mt-0.5"
              />
            ) : null}
            {/* Carryover counter chip — only renders when at least one
                active carryover would surface on this exact appointment
                (server-side count already excludes snoozed-past-this).
                Sits at the end of the icon row so it doesn't push the
                customer name off on a tight tile. */}
            {carryoverCount > 0 ? (
              <span
                onPointerDown={(e) => e.stopPropagation()}
                className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-attention-line bg-attention-soft px-1.5 py-0.5 text-[11px] font-semibold leading-none text-attention-text"
                aria-label={t('tile.carryover_chip_aria', {
                  count: carryoverCount,
                })}
                title={t('tile.carryover_chip_aria', {
                  count: carryoverCount,
                })}
              >
                <Pin aria-hidden className="size-2.5" />
                {carryoverCount}
              </span>
            ) : null}
          </div>
          {/* opacity-90, not 80: compounded with muted-foreground on the
              muted completed/cancelled grounds this reads 5.75:1 — the
              80% value sat at 4.49:1, under the §1.2 4.5 floor. */}
          <div className="truncate opacity-90">{subtitle}</div>
        </div>
      </div>

      {/* Resize handle — only catches pointer events in the bottom 8px
          strip so the rest of the block stays drag-to-move. */}
      <div
        onPointerDown={handleResizePointerDown}
        className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize"
        aria-label={t('hint.resize')}
      />
    </div>
  )
}

// ─── Mobile week strip ─────────────────────────────────────────────
// Horizontal row of 7 day-pills (Mon-Sun) shown below lg. Acts as the
// at-a-glance calendar that the WeekGrid provides on desktop — each
// pill shows day-of-week, the date number, a tiny dot per appointment
// (capped at three), and is tappable to set the focused day. The
// currently focused day gets a filled ink background (§2 human-lane
// selection); "today" gets an ink ring.

function WeekStrip({
  weekStart,
  anchor,
  rows,
  onSelectDay,
}: {
  weekStart: Date
  anchor: Date
  rows: AppointmentRow[]
  onSelectDay: (day: Date) => void
}) {
  const { t } = useTranslation('appointments')
  const today = new Date()

  // Render Mon-Sun (7 days) — the WeekGrid is Mon-Sat for legacy
  // reasons, but mobile users expect a full 7-day week.
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  )

  // Bucket appointments by local day so we can show a per-pill count.
  const countsByDay = useMemo(() => {
    const map = new Map<string, number>()
    for (const r of rows) {
      const d = new Date(r.starts_at)
      if (Number.isNaN(d.getTime())) continue
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
      map.set(key, (map.get(key) ?? 0) + 1)
    }
    return map
  }, [rows])

  return (
    <div className="rounded-[14px] border border-border bg-card p-2">
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
          const count = countsByDay.get(key) ?? 0
          const isAnchor = isSameDay(d, anchor)
          const isToday = isSameDay(d, today)
          const dayLabel = d.toLocaleDateString(undefined, {
            weekday: 'short',
          })

          const pillClass = isAnchor
            ? 'bg-foreground text-background shadow-1'
            : isToday
              ? 'bg-background ring-1 ring-foreground text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted'
              : 'bg-background text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted'

          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => onSelectDay(d)}
              aria-label={t('week_strip.select_day', {
                date: d.toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                }),
                count,
                defaultValue: `${d.toLocaleDateString()} — ${count} appointments`,
              })}
              aria-pressed={isAnchor}
              className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-[10px] px-1 py-2 text-xs font-medium transition-[background-color,color] ${pillClass}`}
            >
              <span
                className={
                  isAnchor
                    ? 'text-[11px] font-semibold opacity-90'
                    : 'text-[11px] font-semibold text-muted-foreground'
                }
              >
                {dayLabel}
              </span>
              <span className="text-base font-semibold leading-none">
                {d.getDate()}
              </span>
              <span className="flex h-1.5 items-center gap-0.5" aria-hidden>
                {count === 0 ? (
                  <span className="h-1.5 w-1.5" />
                ) : (
                  Array.from({ length: Math.min(count, 3) }, (_, i) => (
                    <span
                      key={i}
                      className={
                        isAnchor
                          ? 'h-1 w-1 rounded-full bg-background/80'
                          : 'h-1 w-1 rounded-full bg-saved'
                      }
                    />
                  ))
                )}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── Day list view ──────────────────────────────────────────────────

function DayList({
  day,
  rows,
  carryoverCounts,
  onEmptyCta,
  onAppointmentClick,
}: {
  day: Date
  rows: AppointmentRow[]
  carryoverCounts: Record<string, number>
  onEmptyCta: () => void
  onAppointmentClick: (id: string) => void
}) {
  const { t } = useTranslation('appointments')
  const dayRows = useMemo(() => {
    return rows
      .filter((r) => {
        const d = new Date(r.starts_at)
        return isSameDay(d, day)
      })
      .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
  }, [rows, day])

  if (dayRows.length === 0) {
    return (
      <div className="rounded-[14px] border border-dashed border-border bg-muted/30 p-12 text-center text-sm text-muted-foreground">
        <div>{t('day.empty')}</div>
        <Button
          variant="outline"
          size="sm"
          onClick={onEmptyCta}
          className="mt-4 gap-2"
        >
          <Plus className="size-4" />
          {t('day.empty_cta')}
        </Button>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-[14px] border border-border bg-card">
      <div className="border-b border-border bg-muted px-4 py-2 text-xs text-muted-foreground">
        {t('day.summary', { count: dayRows.length })}
      </div>
      <ul className="divide-y divide-border">
        {dayRows.map((r) => (
          <DayListItem
            key={r.id}
            row={r}
            carryoverCount={carryoverCounts[r.id] ?? 0}
            onClick={() => onAppointmentClick(r.id)}
          />
        ))}
      </ul>
    </div>
  )
}

function DayListItem({
  row,
  carryoverCount,
  onClick,
}: {
  row: AppointmentRow
  carryoverCount: number
  onClick: () => void
}) {
  const { t } = useTranslation('appointments')
  const styles = STATUS_STYLES[row.status] ?? STATUS_STYLES.scheduled!
  const vehicleLabel = formatVehicleLine(
    row.vehicle_year,
    row.vehicle_make,
    row.vehicle_model,
  )
  const durationMin = durationMinutesBetween(row.starts_at, row.ends_at)

  // Row uses a clickable wrapper (div + role/keyboard) instead of a
  // <button>, so we can nest a real <button> for the Copy VIN action
  // without producing invalid nested-interactive HTML.
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className="flex w-full cursor-pointer items-start gap-4 px-4 py-3 text-left transition-[background-color,transform] focus:outline-none focus:ring-2 focus:ring-ring/30 active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
      >
        <div className="w-20 shrink-0 font-mono text-sm font-medium text-foreground">
          {formatTimeShort(row.starts_at)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-[620] text-foreground">
              {row.customer_name ?? t('card.no_customer')}
            </span>
            <Badge
              className={`${styles.chip} rounded-full border px-[9px] text-[12px] font-[650]`}
            >
              {t(`status.${row.status}`, { defaultValue: row.status })}
            </Badge>
            {/* Carryover counter — same chip the WeekGrid block uses,
                so the visual rhymes across desktop and mobile. */}
            {carryoverCount > 0 ? (
              <span
                className="inline-flex items-center gap-0.5 rounded-full border border-attention-line bg-attention-soft px-2 py-0.5 text-[11px] font-semibold leading-none text-attention-text"
                aria-label={t('tile.carryover_chip_aria', {
                  count: carryoverCount,
                })}
              >
                <Pin aria-hidden className="size-2.5" />
                {carryoverCount}
              </span>
            ) : null}
            <EditableTagsReadOnly
              tags={mergeAppointmentTagChips(row)}
              size="xs"
            />
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="truncate">
              {vehicleLabel && row.vehicle_id ? (
                <Link
                  to={`/vehicles/${row.vehicle_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="underline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
                >
                  {vehicleLabel}
                </Link>
              ) : (
                vehicleLabel || t('card.no_vehicle')
              )}
              {row.vehicle_plate ? ` · ${row.vehicle_plate}` : null}
              {row.customer_phone ? (
                <>
                  {' · '}
                  <PhoneLink phone={row.customer_phone} />
                </>
              ) : null}
            </span>
            {row.vehicle_vin ? (
              <CopyVinButton vin={row.vehicle_vin} variant="tile" />
            ) : null}
          </div>
          {row.notes ? (
            <p className="mt-1 text-sm text-foreground">{row.notes}</p>
          ) : null}
        </div>
        <div className="text-xs text-ink-3">
          {t('card.duration_minutes', { count: durationMin })}
        </div>
      </div>
    </li>
  )
}

// ─── Inline vehicle create (VIN, photo, or manual) ─────────────────
//
// Reusable mini-form embedded in both the New and Edit appointment
// dialogs. Three paths to a saved vehicle row:
//
//   1. **Scan VIN sticker** — upload a photo of the doorjamb/dash VIN
//      sticker. The Worker passes it to Anthropic Sonnet vision, which
//      returns the 17-char VIN + a confidence rating + an R2 key for
//      the photo. The VIN populates the form and triggers an NHTSA
//      lookup for year/make/model/engine.
//
//   2. **Type a VIN** — the same NHTSA decode fires on a complete VIN,
//      so the user only confirms.
//
//   3. **Manual** — fill year + make + model directly. Skip the VIN.
//
// On submit, POST /api/vehicles with all known fields (including the
// photo's R2 key when present) and call onCreated(id). The parent
// dialog is responsible for showing/hiding this component and for
// auto-selecting the new vehicle once it's saved.

type FromPhotoResponse = {
  vin: string | null
  confidence: 'high' | 'medium' | 'low'
  photo_r2_key: string
  reason?: string
}

function InlineVehicleCreate({
  customerId,
  onCancel,
  onCreated,
}: {
  customerId: string
  onCancel: () => void
  onCreated: (vehicleId: string) => void
}) {
  const { t } = useTranslation('appointments')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  const [vin, setVin] = useState('')
  const [year, setYear] = useState('')
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [engine, setEngine] = useState('')
  const [plate, setPlate] = useState('')

  // VIN-decoding state is sourced from a useQuery below (loading + error
  // come from query.isFetching / query.error); kept as no extra useState
  // so the strict effect rule has nothing to flag in the kickoff path.

  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [photoR2Key, setPhotoR2Key] = useState<string | null>(null)
  const [photoConfidence, setPhotoConfidence] = useState<string | null>(null)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [creating, setCreating] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Preview URL lifecycle — revoke the blob URL when the file changes.
  // The null branch is a derived-in-render reset (Pattern A) so the
  // effect only runs when a file actually exists. The remaining setState
  // pairs a real browser resource (URL.createObjectURL) with its
  // revoke-on-cleanup partner, which is genuine external side-effect
  // work that must live in an effect.
  const [prevPhotoFile, setPrevPhotoFile] = useState<File | null>(null)
  if (photoFile !== prevPhotoFile) {
    setPrevPhotoFile(photoFile)
    if (!photoFile && photoPreview !== null) setPhotoPreview(null)
  }
  useEffect(() => {
    if (!photoFile) return
    const url = URL.createObjectURL(photoFile)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- paired with revokeObjectURL cleanup; genuine browser-resource lifecycle
    setPhotoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [photoFile])

  // Decode on a complete VIN via TanStack Query — query.isFetching
  // becomes the "decoding" indicator and query.error supplies the error
  // message, so there's no setState-in-effect kickoff. The success path
  // still needs to seed the editable year/make/model/engine fields, and
  // that auto-fill is done with a Pattern A "reset state on data change"
  // check in render against the last decoded VIN we applied.
  const trimmedVin = vin.trim().toUpperCase()
  const vinIsValid = isValidVin(trimmedVin)
  const vinDecodeQuery = useQuery({
    queryKey: ['vin-decode', trimmedVin],
    enabled: vinIsValid,
    queryFn: ({ signal }) => decodeVin(trimmedVin, { signal }),
  })
  const vinDecoding = vinIsValid && vinDecodeQuery.isFetching
  const vinError =
    vinIsValid && vinDecodeQuery.error
      ? vinDecodeQuery.error instanceof Error
        ? vinDecodeQuery.error.message
        : 'vin_decode_failed'
      : null
  const [appliedDecodeVin, setAppliedDecodeVin] = useState<string | null>(null)
  if (
    vinIsValid &&
    vinDecodeQuery.data &&
    appliedDecodeVin !== trimmedVin
  ) {
    setAppliedDecodeVin(trimmedVin)
    const d = vinDecodeQuery.data
    if (d.year !== null) setYear(String(d.year))
    if (d.make) setMake(d.make)
    if (d.model) setModel(d.model)
    if (d.engine) setEngine(d.engine)
  }

  async function handlePhotoSelected(file: File) {
    setPhotoFile(file)
    setPhotoError(null)
    setPhotoR2Key(null)
    setPhotoConfidence(null)
    setPhotoUploading(true)
    try {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const fd = new FormData()
      fd.append('photo', file)
      const res = await fetch('/api/vehicles/from-photo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      const data = (await res.json()) as FromPhotoResponse
      setPhotoR2Key(data.photo_r2_key)
      setPhotoConfidence(data.confidence)
      if (data.vin) setVin(data.vin) // triggers NHTSA decode
      else setPhotoError(t('new.vehicle.photo_no_vin'))
    } catch (err) {
      setPhotoError(
        err instanceof Error ? err.message : t('new.vehicle.photo_failed'),
      )
    } finally {
      setPhotoUploading(false)
    }
  }

  async function handleSave() {
    setSubmitError(null)
    const trimmedVin = vin.trim().toUpperCase()
    if (!trimmedVin && !make.trim() && !model.trim()) {
      setSubmitError(t('new.vehicle.payload_required'))
      return
    }
    if (trimmedVin && !isValidVin(trimmedVin)) {
      setSubmitError(t('new.vehicle.invalid_vin'))
      return
    }
    setCreating(true)
    try {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/vehicles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_id: customerId,
          vin: trimmedVin || null,
          year: year.trim() ? Number.parseInt(year, 10) : null,
          make: make.trim() || null,
          model: model.trim() || null,
          engine: engine.trim() || null,
          plate: plate.trim() || null,
          photo_r2_key: photoR2Key,
        }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      const created = (await res.json()) as { id: string }
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      onCreated(created.id)
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : t('new.vehicle.create_failed'),
      )
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted p-3">
      {/* Photo scan path — top of the form so it reads as the
          preferred fast path. Clicking the button opens the native
          file picker; we accept any image type (camera-capable on
          mobile, gallery-capable on desktop). */}
      <div className="space-y-1">
        <Label className="text-xs font-medium">
          {t('new.vehicle.scan_label')}
        </Label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) handlePhotoSelected(f)
            // Reset so re-picking the same file fires `onChange` again.
            if (fileInputRef.current) fileInputRef.current.value = ''
          }}
        />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={photoUploading}
            className="gap-2"
          >
            <Camera className="size-4" />
            {photoUploading
              ? t('new.vehicle.photo_uploading')
              : photoR2Key
                ? t('new.vehicle.photo_replace')
                : t('new.vehicle.photo_scan')}
          </Button>
          {photoPreview ? (
            <img
              src={photoPreview}
              alt="VIN sticker preview"
              className="h-10 w-10 rounded border border-border object-cover"
            />
          ) : null}
          {photoConfidence ? (
            // Confidence is a "check my work" signal, not a saved state —
            // §2 keeps green out of it: calm neutral / amber / red.
            <span
              className={
                photoConfidence === 'high'
                  ? 'text-[11px] font-medium text-muted-foreground'
                  : photoConfidence === 'medium'
                    ? 'text-[11px] font-medium text-attention-text'
                    : 'text-[11px] font-medium text-destructive'
              }
            >
              {t(`new.vehicle.photo_confidence.${photoConfidence}`)}
            </span>
          ) : null}
        </div>
        {photoError ? (
          <div className="text-[11px] text-destructive">{photoError}</div>
        ) : null}
      </div>

      <div className="space-y-1">
        <Label htmlFor="iv-vin" className="text-xs font-medium">
          {t('new.vehicle.vin')}
        </Label>
        <Input
          id="iv-vin"
          value={vin}
          onChange={(e) => setVin(e.target.value.toUpperCase())}
          placeholder={t('new.vehicle.vin_placeholder')}
          maxLength={17}
          className="font-mono uppercase"
        />
        <p className="text-[11px] text-muted-foreground">
          {vinDecoding
            ? t('new.vehicle.vin_decoding')
            : vinError
              ? t('new.vehicle.vin_error')
              : t('new.vehicle.vin_hint')}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label htmlFor="iv-year" className="text-xs font-medium">
            {t('new.vehicle.year')}
          </Label>
          <Input
            id="iv-year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="2018"
            inputMode="numeric"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="iv-make" className="text-xs font-medium">
            {t('new.vehicle.make')}
          </Label>
          <Input
            id="iv-make"
            value={make}
            onChange={(e) => setMake(e.target.value)}
            placeholder="Toyota"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="iv-model" className="text-xs font-medium">
            {t('new.vehicle.model')}
          </Label>
          <Input
            id="iv-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Camry"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor="iv-engine" className="text-xs font-medium">
            {t('new.vehicle.engine')}
          </Label>
          <Input
            id="iv-engine"
            value={engine}
            onChange={(e) => setEngine(e.target.value)}
            placeholder="2.5L 4cyl"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="iv-plate" className="text-xs font-medium">
            {t('new.vehicle.plate')}
          </Label>
          <Input
            id="iv-plate"
            value={plate}
            onChange={(e) => setPlate(e.target.value.toUpperCase())}
            placeholder="BAYV 532"
            className="uppercase"
          />
        </div>
      </div>

      {submitError ? (
        <div className="text-xs text-destructive">{submitError}</div>
      ) : null}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={creating}
        >
          {tCommon('cancel')}
        </Button>
        <Button type="button" size="sm" onClick={handleSave} disabled={creating}>
          {creating ? t('new.vehicle.creating') : t('new.vehicle.create')}
        </Button>
      </div>
    </div>
  )
}

// ─── New appointment dialog ────────────────────────────────────────

function NewAppointmentDialog({
  open,
  onOpenChange,
  prefillStartLocal,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  prefillStartLocal: string | null
  onCreated: () => void
}) {
  const { t } = useTranslation('appointments')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()

  const [customerId, setCustomerId] = useState<string | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerOpen, setCustomerOpen] = useState(false)
  const [showNewCustomer, setShowNewCustomer] = useState(false)
  const [newCustomerName, setNewCustomerName] = useState('')
  const [newCustomerPhone, setNewCustomerPhone] = useState('')
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [customerError, setCustomerError] = useState<string | null>(null)
  const customerBoxRef = useRef<HTMLDivElement | null>(null)

  const [vehicleId, setVehicleId] = useState<string | null>(null)
  const [showNewVehicle, setShowNewVehicle] = useState(false)

  const [startsAt, setStartsAt] = useState(defaultStartLocal())
  const [durationMinutes, setDurationMinutes] = useState('60')
  const [jobDescription, setJobDescription] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset everything when the dialog opens — including pre-fill from
  // a clicked time slot in the week grid. Pattern A "reset state on
  // prop change": React handles in-render setState by discarding the
  // current paint and re-rendering with the reset values, so there's
  // no flicker. Fires on the open=false→true edge and on a fresh
  // prefillStartLocal while open.
  const [prevDialogReset, setPrevDialogReset] = useState<{
    open: boolean
    prefillStartLocal: string | null
  }>({ open, prefillStartLocal })
  if (
    open &&
    (!prevDialogReset.open ||
      prevDialogReset.prefillStartLocal !== prefillStartLocal)
  ) {
    setPrevDialogReset({ open, prefillStartLocal })
    setCustomerId(null)
    setCustomerSearch('')
    setCustomerOpen(false)
    setShowNewCustomer(false)
    setNewCustomerName('')
    setNewCustomerPhone('')
    setCustomerError(null)
    setVehicleId(null)
    setShowNewVehicle(false)
    setStartsAt(prefillStartLocal ?? defaultStartLocal())
    setDurationMinutes('60')
    setJobDescription('')
    setSubmitError(null)
  } else if (!open && prevDialogReset.open) {
    setPrevDialogReset({ open, prefillStartLocal })
  }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        customerBoxRef.current &&
        !customerBoxRef.current.contains(e.target as Node)
      ) {
        setCustomerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const customersQuery = useQuery({
    queryKey: ['customers'],
    enabled: isLoaded && open,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/customers', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as CustomerListRow[]
    },
  })

  const vehiclesQuery = useQuery({
    queryKey: ['vehicles'],
    enabled: isLoaded && open,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/vehicles', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as VehicleListRow[]
    },
  })

  const filteredCustomers = useMemo(() => {
    const all = customersQuery.data ?? []
    const q = customerSearch.trim().toLowerCase()
    if (!q) return all.slice(0, 8)
    return all
      .filter((c) => {
        return (
          c.name.toLowerCase().includes(q) ||
          (c.phone ?? '').toLowerCase().includes(q) ||
          (c.email ?? '').toLowerCase().includes(q)
        )
      })
      .slice(0, 8)
  }, [customersQuery.data, customerSearch])

  const selectedCustomer = useMemo(() => {
    if (!customerId) return null
    return (customersQuery.data ?? []).find((c) => c.id === customerId) ?? null
  }, [customerId, customersQuery.data])

  const eligibleVehicles = useMemo(() => {
    if (!customerId) return []
    return (vehiclesQuery.data ?? []).filter(
      (v) => v.customer_id === customerId,
    )
  }, [customerId, vehiclesQuery.data])

  // Reset/auto-pick vehicle when the chosen customer, eligible list, or
  // current vehicle selection changes (Pattern A in render). Mirrors
  // the original effect deps exactly so behavior is preserved:
  //   1. Customer cleared → clear the vehicle selection.
  //   2. Vehicle list is exactly 1 → auto-pick that vehicle (even if
  //      the user just picked "unset" — matches the previous effect's
  //      restore-to-only behavior).
  //   3. Currently-selected vehicle is no longer eligible → clear it.
  // Done synchronously in render with React's in-render setState
  // discard-and-rerun semantics, so no flicker and no useEffect.
  const eligibleVehicleIdsKey = eligibleVehicles.map((v) => v.id).join('|')
  const [prevVehicleReconcile, setPrevVehicleReconcile] = useState<{
    customerId: string | null
    eligibleVehicleIdsKey: string
    vehicleId: string | null
  }>({ customerId, eligibleVehicleIdsKey, vehicleId })
  if (
    prevVehicleReconcile.customerId !== customerId ||
    prevVehicleReconcile.eligibleVehicleIdsKey !== eligibleVehicleIdsKey ||
    prevVehicleReconcile.vehicleId !== vehicleId
  ) {
    setPrevVehicleReconcile({ customerId, eligibleVehicleIdsKey, vehicleId })
    if (!customerId) {
      if (vehicleId !== null) setVehicleId(null)
    } else if (eligibleVehicles.length === 1) {
      const only = eligibleVehicles[0]!.id
      if (vehicleId !== only) setVehicleId(only)
    } else if (
      vehicleId &&
      !eligibleVehicles.some((v) => v.id === vehicleId)
    ) {
      setVehicleId(null)
    }
  }

  async function handleCreateCustomer() {
    setCustomerError(null)
    if (!newCustomerName.trim()) {
      setCustomerError(t('new.customer.name_required'))
      return
    }
    setCreatingCustomer(true)
    try {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newCustomerName.trim(),
          phone: newCustomerPhone.trim() || null,
        }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      const created = (await res.json()) as CustomerListRow
      await queryClient.invalidateQueries({ queryKey: ['customers'] })
      setCustomerId(created.id)
      setCustomerSearch(created.name)
      setShowNewCustomer(false)
      setCustomerOpen(false)
      setNewCustomerName('')
      setNewCustomerPhone('')
    } catch (err) {
      setCustomerError(
        err instanceof Error ? err.message : t('new.customer.create_failed'),
      )
    } finally {
      setCreatingCustomer(false)
    }
  }

  // Inline vehicle-create state lives inside <InlineVehicleCreate>
  // (see component above). NewAppointmentDialog only tracks the
  // show/hide flag — the form itself manages its own VIN, photo,
  // and manual-entry state.

  const createMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const startIso = localToIso(startsAt)
      const minutes = Math.max(15, Number.parseInt(durationMinutes, 10) || 60)
      const endDate = new Date(startIso)
      endDate.setMinutes(endDate.getMinutes() + minutes)

      const payload = {
        customer_id: customerId,
        vehicle_id: vehicleId,
        starts_at: startIso,
        ends_at: endDate.toISOString(),
        notes: jobDescription.trim() || null,
      }

      const res = await fetch('/api/appointments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as { id: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      onCreated()
    },
    onError: (err) => {
      setSubmitError(err instanceof Error ? err.message : t('new.error_submit'))
    },
  })

  const canSubmit =
    !!customerId && !!startsAt && !createMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('new.title')}</DialogTitle>
          <DialogDescription>{t('new.description')}</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!canSubmit) return
            createMutation.mutate()
          }}
        >
          {/* Customer combobox + inline "+ new customer" */}
          <div ref={customerBoxRef} className="space-y-1">
            <Label htmlFor="appt-customer">{t('new.fields.customer')}</Label>
            {showNewCustomer ? (
              <div className="space-y-2 rounded-md border border-border bg-muted p-3">
                <div className="space-y-1">
                  <Label
                    htmlFor="new-cust-name"
                    className="text-xs font-medium"
                  >
                    {t('new.customer.name')}
                  </Label>
                  <Input
                    id="new-cust-name"
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                    placeholder={t('new.customer.name_placeholder')}
                    autoFocus
                  />
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="new-cust-phone"
                    className="text-xs font-medium"
                  >
                    {t('new.customer.phone')}
                  </Label>
                  <Input
                    id="new-cust-phone"
                    value={newCustomerPhone}
                    onChange={(e) => setNewCustomerPhone(e.target.value)}
                    placeholder="(416) 555-0123"
                    type="tel"
                  />
                </div>
                {customerError ? (
                  <div className="text-xs text-destructive">{customerError}</div>
                ) : null}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowNewCustomer(false)
                      setCustomerError(null)
                    }}
                    disabled={creatingCustomer}
                  >
                    {tCommon('cancel')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateCustomer}
                    disabled={creatingCustomer || !newCustomerName.trim()}
                  >
                    {creatingCustomer
                      ? t('new.customer.creating')
                      : t('new.customer.create')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <Input
                  id="appt-customer"
                  autoComplete="off"
                  value={
                    selectedCustomer ? selectedCustomer.name : customerSearch
                  }
                  onFocus={() => setCustomerOpen(true)}
                  onChange={(e) => {
                    setCustomerId(null)
                    setCustomerSearch(e.target.value)
                    setCustomerOpen(true)
                  }}
                  placeholder={t('new.fields.customer_placeholder')}
                />

                {customerOpen ? (
                  <div className="absolute z-40 mt-1 w-full rounded-md border border-border bg-popover py-1 shadow-2">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-ink-3">
                        {customersQuery.isLoading
                          ? tCommon('loading')
                          : t('new.customer.empty')}
                      </div>
                    ) : (
                      filteredCustomers.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                          onClick={() => {
                            setCustomerId(c.id)
                            setCustomerSearch('')
                            setCustomerOpen(false)
                          }}
                        >
                          <span className="truncate">{c.name}</span>
                          <span className="font-mono text-xs text-ink-3">
                            {formatPhone(c.phone)}
                          </span>
                        </button>
                      ))
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        // Seed the new-customer name from whatever they
                        // typed so they don't retype it.
                        setNewCustomerName(customerSearch.trim())
                        setShowNewCustomer(true)
                      }}
                      className="mt-1 flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm font-medium text-foreground active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                    >
                      <Plus className="size-3.5" />
                      {t('new.customer.create_inline')}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Vehicle selector + inline create.
              Vehicle inline-create lives in <InlineVehicleCreate> —
              three paths in one component: VIN (NHTSA auto-decode),
              photo of VIN sticker (Anthropic Sonnet vision), or
              manual year + make + model. */}
          {customerId ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="appt-vehicle">{t('new.fields.vehicle')}</Label>
                {!showNewVehicle ? (
                  <button
                    type="button"
                    onClick={() => setShowNewVehicle(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
                  >
                    <Plus className="size-3.5" />
                    {t('new.vehicle.create_inline')}
                  </button>
                ) : null}
              </div>

              {showNewVehicle ? (
                <InlineVehicleCreate
                  customerId={customerId}
                  onCancel={() => setShowNewVehicle(false)}
                  onCreated={(id) => {
                    setVehicleId(id)
                    setShowNewVehicle(false)
                  }}
                />
              ) : eligibleVehicles.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-3 text-xs text-ink-3">
                  {t('new.vehicle.none')}
                </div>
              ) : (
                <select
                  id="appt-vehicle"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={vehicleId ?? ''}
                  onChange={(e) => setVehicleId(e.target.value || null)}
                >
                  <option value="">{t('new.vehicle.unset')}</option>
                  {eligibleVehicles.map((v) => {
                    const label = [v.year, v.make, v.model]
                      .filter(Boolean)
                      .join(' ')
                    return (
                      <option key={v.id} value={v.id}>
                        {label || v.id}
                        {v.plate ? ` · ${v.plate}` : ''}
                      </option>
                    )
                  })}
                </select>
              )}
            </div>
          ) : null}

          {/* Date + duration. Stacks on mobile — the native datetime
              picker renders a long label ("May 19, 2026 at 2:00 AM") that
              would otherwise overflow into the duration cell at 375px. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
            <div className="space-y-1">
              <Label htmlFor="appt-start">{t('new.fields.starts_at')}</Label>
              <Input
                id="appt-start"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="appt-duration">
                {t('new.fields.duration_minutes')}
              </Label>
              <Input
                id="appt-duration"
                type="number"
                min={15}
                step={15}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
              />
            </div>
          </div>

          {/* Job description (mapped to `notes` on the wire) */}
          <div className="space-y-1">
            <Label htmlFor="appt-job">{t('new.fields.job_description')}</Label>
            <Textarea
              id="appt-job"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              rows={3}
              placeholder={t('new.fields.job_placeholder')}
            />
          </div>

          {submitError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive-soft p-3 text-xs text-destructive">
              {submitError}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit} className="gap-2">
              <Plus className="size-4" />
              {createMutation.isPending
                ? t('new.submitting')
                : t('new.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Appointment detail / edit dialog ───────────────────────────────
//
// Opens when the user clicks an existing appointment (block in the
// week grid OR row in the day list). Allows editing every field that
// POST /api/appointments accepts, plus quick-action buttons to
// transition status without re-saving the whole form. Cancel button
// soft-cancels (PATCH status='cancelled') rather than hard-deleting,
// so the audit trail stays intact.

const STATUS_QUICK_ACTIONS: AppointmentStatus[] = [
  'scheduled',
  'confirmed',
  'arrived',
  'in_progress',
  'completed',
  'no_show',
]

function AppointmentDetailDialog({
  row,
  open,
  onOpenChange,
  onSaved,
  onDeleted,
}: {
  row: AppointmentRow | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
  onDeleted: () => void
}) {
  const { t } = useTranslation('appointments')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()

  const [customerId, setCustomerId] = useState<string | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerOpen, setCustomerOpen] = useState(false)
  const customerBoxRef = useRef<HTMLDivElement | null>(null)

  const [vehicleId, setVehicleId] = useState<string | null>(null)
  const [showNewVehicle, setShowNewVehicle] = useState(false)
  const [startsAt, setStartsAt] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('60')
  const [status, setStatus] = useState<AppointmentStatus>('scheduled')
  const [jobDescription, setJobDescription] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Odometer-from-photo modal launched from the vehicle summary card.
  // OWNER / ADMIN / MECHANIC only — same gate as the vehicle detail
  // page; receptionists see the mileage but can't update it.
  const [odometerPhotoOpen, setOdometerPhotoOpen] = useState(false)
  const canUpdateMileageFromPhoto = useCanApproveEstimate()

  // Hydrate the form whenever a different appointment is opened.
  // Pattern A "reset state on prop change": when the `row` reference
  // changes (parent passed a new appointment, or the list refetched and
  // produced a fresh object), reseed the editable fields in render.
  // React discards the in-flight paint and re-renders with the new
  // state, so there's no flicker and no setState-in-effect.
  const [prevRow, setPrevRow] = useState<AppointmentRow | null>(row)
  if (row !== prevRow) {
    setPrevRow(row)
    if (row) {
      setCustomerId(row.customer_id)
      setCustomerSearch('')
      setCustomerOpen(false)
      setVehicleId(row.vehicle_id)
      setShowNewVehicle(false)
      setStartsAt(toLocalInputValue(new Date(row.starts_at)))
      setDurationMinutes(
        String(durationMinutesBetween(row.starts_at, row.ends_at) || 60),
      )
      setStatus(row.status)
      setJobDescription(row.notes ?? '')
      setSubmitError(null)
    }
  }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        customerBoxRef.current &&
        !customerBoxRef.current.contains(e.target as Node)
      ) {
        setCustomerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const customersQuery = useQuery({
    queryKey: ['customers'],
    enabled: isLoaded && open,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/customers', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as CustomerListRow[]
    },
  })

  const vehiclesQuery = useQuery({
    queryKey: ['vehicles'],
    enabled: isLoaded && open,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/vehicles', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as VehicleListRow[]
    },
  })

  const filteredCustomers = useMemo(() => {
    const all = customersQuery.data ?? []
    const q = customerSearch.trim().toLowerCase()
    if (!q) return all.slice(0, 8)
    return all
      .filter((c) => {
        return (
          c.name.toLowerCase().includes(q) ||
          (c.phone ?? '').toLowerCase().includes(q) ||
          (c.email ?? '').toLowerCase().includes(q)
        )
      })
      .slice(0, 8)
  }, [customersQuery.data, customerSearch])

  const selectedCustomer = useMemo(() => {
    if (!customerId) return null
    return (customersQuery.data ?? []).find((c) => c.id === customerId) ?? null
  }, [customerId, customersQuery.data])

  const eligibleVehicles = useMemo(() => {
    if (!customerId) return []
    return (vehiclesQuery.data ?? []).filter(
      (v) => v.customer_id === customerId,
    )
  }, [customerId, vehiclesQuery.data])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!row) throw new Error('no_row')
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const startIso = localToIso(startsAt)
      const minutes = Math.max(15, Number.parseInt(durationMinutes, 10) || 60)
      const endDate = new Date(startIso)
      endDate.setMinutes(endDate.getMinutes() + minutes)

      const payload = {
        customer_id: customerId,
        vehicle_id: vehicleId,
        starts_at: startIso,
        ends_at: endDate.toISOString(),
        status,
        notes: jobDescription.trim() || null,
      }

      const res = await fetch(`/api/appointments/${row.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as { id: string }
    },
    onSuccess: () => {
      onSaved()
      onOpenChange(false)
    },
    onError: (err) => {
      setSubmitError(
        err instanceof Error ? err.message : t('detail.error_save'),
      )
    },
  })

  // Quick-action status change — PATCH the status only without
  // touching the rest of the form. The mutation reuses the same
  // endpoint as save; on success we update local state to match.
  const statusMutation = useMutation({
    mutationFn: async (next: AppointmentStatus) => {
      if (!row) throw new Error('no_row')
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/appointments/${row.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: next }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return next
    },
    onSuccess: (next) => {
      setStatus(next)
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
    },
  })

  // Cancel = soft-cancel via PATCH status='cancelled'. Keeps the row
  // for audit; the row visually fades on the calendar after this.
  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!row) throw new Error('no_row')
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/appointments/${row.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'cancelled' }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
    },
    onSuccess: () => {
      onDeleted()
    },
    onError: (err) => {
      setSubmitError(
        err instanceof Error ? err.message : t('detail.error_cancel'),
      )
    },
  })

  if (!row) return null

  const canSubmit = !!startsAt && !saveMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {/* Odometer-from-photo modal — sibling to the parent Dialog so
            opening it doesn't collapse the appointment dialog under it.
            Passes the appointment id through to the modal so the
            history row gets tied to this visit on save. */}
        {row.vehicle_id ? (
          <OdometerPhotoUploadModal
            vehicleId={row.vehicle_id}
            currentMileage={row.vehicle_mileage}
            appointmentId={row.id}
            open={odometerPhotoOpen}
            onOpenChange={setOdometerPhotoOpen}
          />
        ) : null}

        <DialogHeader>
          <DialogTitle>{t('detail.title')}</DialogTitle>
          <DialogDescription>
            {t('detail.description', { id: row.id })}
          </DialogDescription>
        </DialogHeader>

        {/* Carryover banner — Eric brain v2. Surfaces any 'active'
            vehicle_carryover_notes for this appointment's vehicle so
            the mechanic sees "from your last visit on YYYY-MM-DD: ..."
            before doing anything else. Auto-acknowledges when this
            appointment moves to status='completed' (server-side hook). */}
        {row.vehicle_id ? (
          <CarryoverBanner
            vehicleId={row.vehicle_id}
            sourceAppointmentId={row.id}
            onAck={() => queryClient.invalidateQueries({ queryKey: ['appointments'] })}
          />
        ) : null}

        {/* Status quick-actions row — single-click transitions for the
            common shop-floor lifecycle. Save below covers everything
            else (rescheduling, notes, vehicle change, etc.). */}
        <div className="flex flex-wrap items-center gap-1.5">
          {STATUS_QUICK_ACTIONS.map((s) => {
            const active = status === s
            // §4.9: lifecycle chips are neutral — the ACTIVE status is an
            // ink fill (a human decision, §2), never green.
            return (
              <button
                key={s}
                type="button"
                onClick={() => statusMutation.mutate(s)}
                disabled={statusMutation.isPending}
                className={
                  active
                    ? 'rounded-full border border-foreground bg-foreground px-[9px] py-[3px] text-[12px] font-[650] text-background'
                    : 'rounded-full border border-border bg-background px-[9px] py-[3px] text-[12px] font-[650] text-muted-foreground transition-colors [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground'
                }
              >
                {t(`status.${s}`)}
              </button>
            )
          })}
        </div>

        {/* Vehicle summary card — surfaces year+make+model and the full
            VIN (in monospace) with a copy button, so the mechanic can
            grab the VIN while the dialog is open instead of closing,
            scrolling, then reopening just to read it off the tile. */}
        {row.vehicle_id ? (
          <div className="rounded-[10px] border border-border bg-muted px-3 py-2.5">
            <div className="text-[13px] font-semibold text-muted-foreground">
              {t('detail.vehicle_section')}
            </div>
            <div className="mt-0.5 truncate text-[15px] font-[620] text-foreground">
              <Link
                to={`/vehicles/${row.vehicle_id}`}
                className="underline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
              >
                {formatVehicleLine(
                  row.vehicle_year,
                  row.vehicle_make,
                  row.vehicle_model,
                ) || t('card.no_vehicle')}
              </Link>
              {row.vehicle_plate ? (
                <span className="ml-2 font-normal text-muted-foreground">
                  · {row.vehicle_plate}
                </span>
              ) : null}
            </div>
            {row.vehicle_id ? (
              <div className="mt-1.5">
                <EditableTags
                  entityType="vehicle"
                  entityId={row.vehicle_id}
                  tags={row.vehicle_tag_chips}
                />
              </div>
            ) : null}
            {row.vehicle_vin ? (
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-[12px] font-medium text-muted-foreground">
                  {t('detail.vehicle_vin_label')}
                </span>
                <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1 font-mono text-xs text-foreground ring-1 ring-inset ring-border">
                  {row.vehicle_vin}
                </code>
                <CopyVinButton vin={row.vehicle_vin} variant="dialog" />
              </div>
            ) : (
              <div className="mt-1.5 text-xs italic text-muted-foreground">
                {t('detail.vehicle_no_vin')}
              </div>
            )}

            {/* Mileage row + "Update from photo" affordance. The button
                mounts the same OdometerPhotoUploadModal that the
                vehicle detail page uses, but passes the appointment id
                so the resulting vehicle_mileage_history row is tied
                back to this visit (mileage panel on /vehicles/:id will
                show the appointment chip in a follow-on iteration). */}
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className="text-[12px] font-medium text-muted-foreground">
                {t('detail.vehicle_mileage_label')}
              </span>
              <span className="font-mono text-xs text-foreground">
                {row.vehicle_mileage !== null
                  ? tCommon('units.km', {
                      value: row.vehicle_mileage.toLocaleString(),
                    })
                  : '—'}
              </span>
              {canUpdateMileageFromPhoto ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setOdometerPhotoOpen(true)}
                  className="ml-auto h-7 px-2 text-[11px]"
                >
                  <Camera className="size-3.5" />
                  {t('detail.update_mileage_from_photo')}
                </Button>
              ) : null}
            </div>

            {/* Persistent vehicle notes — Per-vehicle notes Feature.
                Read-only here; edits happen on /vehicles/:id. Sits
                inside the same vehicle summary card so context stays
                grouped: VIN above, notes below. */}
            {row.vehicle_notes && row.vehicle_notes.trim().length > 0 ? (
              <div className="mt-2 rounded-[10px] border border-attention-line bg-attention-soft px-2.5 py-2">
                <div className="flex items-center gap-1.5 text-[12px] font-semibold text-attention-text">
                  <StickyNote className="size-3" />
                  {t('detail.vehicle_notes_header')}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-xs text-foreground">
                  {row.vehicle_notes}
                </p>
                <p className="mt-1 text-[11px] italic text-muted-foreground">
                  {t('detail.vehicle_notes_hint')}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!canSubmit) return
            saveMutation.mutate()
          }}
        >
          {/* Customer combobox */}
          <div ref={customerBoxRef} className="space-y-1">
            <Label htmlFor="appt-edit-customer">
              {t('new.fields.customer')}
            </Label>
            <div className="relative">
              <Input
                id="appt-edit-customer"
                autoComplete="off"
                value={selectedCustomer ? selectedCustomer.name : customerSearch}
                onFocus={() => setCustomerOpen(true)}
                onChange={(e) => {
                  setCustomerId(null)
                  setCustomerSearch(e.target.value)
                  setCustomerOpen(true)
                }}
                placeholder={t('new.fields.customer_placeholder')}
              />
              {customerOpen ? (
                <div className="absolute z-40 mt-1 w-full rounded-md border border-border bg-popover py-1 shadow-2">
                  {filteredCustomers.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-ink-3">
                      {customersQuery.isLoading
                        ? tCommon('loading')
                        : t('new.customer.empty')}
                    </div>
                  ) : (
                    filteredCustomers.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                        onClick={() => {
                          setCustomerId(c.id)
                          setCustomerSearch('')
                          setCustomerOpen(false)
                        }}
                      >
                        <span className="truncate">{c.name}</span>
                        <span className="font-mono text-xs text-ink-3">
                          {formatPhone(c.phone)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>
            {row && row.customer_id ? (
              <EditableTags
                entityType="customer"
                entityId={row.customer_id}
                tags={row.customer_tag_chips}
                className="mt-1"
              />
            ) : null}
          </div>

          {/* Vehicle selector + inline create. Same three-mode component
              the New dialog uses — VIN, photo of VIN sticker, or
              manual year+make+model. */}
          {customerId ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="appt-edit-vehicle">
                  {t('new.fields.vehicle')}
                </Label>
                {!showNewVehicle ? (
                  <button
                    type="button"
                    onClick={() => setShowNewVehicle(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
                  >
                    <Plus className="size-3.5" />
                    {t('new.vehicle.create_inline')}
                  </button>
                ) : null}
              </div>

              {showNewVehicle ? (
                <InlineVehicleCreate
                  customerId={customerId}
                  onCancel={() => setShowNewVehicle(false)}
                  onCreated={(id) => {
                    setVehicleId(id)
                    setShowNewVehicle(false)
                  }}
                />
              ) : eligibleVehicles.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-3 text-xs text-ink-3">
                  {t('new.vehicle.none')}
                </div>
              ) : (
                <select
                  id="appt-edit-vehicle"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={vehicleId ?? ''}
                  onChange={(e) => setVehicleId(e.target.value || null)}
                >
                  <option value="">{t('new.vehicle.unset')}</option>
                  {eligibleVehicles.map((v) => {
                    const label = [v.year, v.make, v.model]
                      .filter(Boolean)
                      .join(' ')
                    return (
                      <option key={v.id} value={v.id}>
                        {label || v.id}
                        {v.plate ? ` · ${v.plate}` : ''}
                      </option>
                    )
                  })}
                </select>
              )}
            </div>
          ) : null}

          {/* Time. Mirrors the New dialog — datetime-local needs the full
              row on mobile so the long pretty-printed date doesn't bleed
              into the duration cell. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
            <div className="space-y-1">
              <Label htmlFor="appt-edit-start">
                {t('new.fields.starts_at')}
              </Label>
              <Input
                id="appt-edit-start"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="appt-edit-duration">
                {t('new.fields.duration_minutes')}
              </Label>
              <Input
                id="appt-edit-duration"
                type="number"
                min={15}
                step={15}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
              />
            </div>
          </div>

          {/* Voice note recorder — Emil dictates from under the car, the
              transcript appends to the job description below. Above the
              textarea so the user sees the new words land into context. */}
          <div className="space-y-1">
            <Label>{t('detail.voice_note_header')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('detail.voice_note_help')}
            </p>
            <VoiceRecorder
              onApply={(text) => {
                setJobDescription((prev) => {
                  const trimmed = prev.trim()
                  if (trimmed.length === 0) return text
                  return `${trimmed}\n\n${text}`
                })
              }}
            />
          </div>

          {/* Job description */}
          <div className="space-y-1">
            <Label htmlFor="appt-edit-job">
              {t('new.fields.job_description')}
            </Label>
            <Textarea
              id="appt-edit-job"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              rows={3}
              placeholder={t('new.fields.job_placeholder')}
            />
          </div>

          {submitError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive-soft p-3 text-xs text-destructive">
              {submitError}
            </div>
          ) : null}

          <DialogFooter className="!justify-between sm:!justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (
                  confirm(
                    t('detail.confirm_cancel', {
                      defaultValue: 'Cancel this appointment?',
                    }),
                  )
                ) {
                  cancelMutation.mutate()
                }
              }}
              disabled={cancelMutation.isPending}
              className="text-destructive [@media(hover:hover)_and_(pointer:fine)]:hover:bg-destructive-soft [@media(hover:hover)_and_(pointer:fine)]:hover:text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              {cancelMutation.isPending
                ? t('detail.cancelling')
                : t('detail.cancel_appointment')}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={saveMutation.isPending}
              >
                {tCommon('cancel')}
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {saveMutation.isPending ? t('detail.saving') : t('detail.save')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Eric brain v2 — carryover note banner.
//
// Lists all 'active' carryover notes for a vehicle and lets the
// mechanic Acknowledge each. Lives only inside AppointmentDetailDialog
// today; v2 will also render a small chip on the day-grid tile.

type CarryoverRow = {
  id: string
  vehicle_id: string
  source_appointment_id: string | null
  body: string
  state: string
  flagged_by_clerk_user_id: string | null
  flagged_by_eric: boolean
  flagged_at: string
  acknowledged_at: string | null
  surfaced_at: string | null
  edit_count: number
  snoozed_past_appointment_id: string | null
  // v2: actual visit date of the source appointment — server-side
  // correlated subquery. Null when source_appointment_id is unset
  // (Eric flagged from SMS without an appointment context); UI falls
  // back to flagged_at in that case.
  source_appointment_starts_at: string | null
}

function CarryoverBanner({
  vehicleId,
  sourceAppointmentId,
  onAck,
}: {
  vehicleId: string
  sourceAppointmentId?: string | null
  onAck: () => void
}) {
  const { t } = useTranslation('appointments')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()
  const [flagOpen, setFlagOpen] = useState(false)
  const [flagText, setFlagText] = useState('')
  const [flagError, setFlagError] = useState<string | null>(null)

  // current_appointment_id is included in the query key so swapping
  // between two different appointment dialogs for the same vehicle
  // gets a fresh fetch (each dialog respects its own snooze filter).
  const carryoversQuery = useQuery({
    queryKey: ['carryovers', vehicleId, 'active', sourceAppointmentId ?? null],
    enabled: isLoaded && !!vehicleId,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const params = new URLSearchParams({ state: 'active' })
      if (sourceAppointmentId) {
        params.set('current_appointment_id', sourceAppointmentId)
      }
      const res = await fetch(
        `/api/vehicles/${vehicleId}/carryover?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) return { carryovers: [] as CarryoverRow[] }
      return (await res.json()) as { carryovers: CarryoverRow[] }
    },
  })

  const ackMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/carryover/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: 'acknowledged' }),
      })
      if (!res.ok) throw new Error(t('detail.carryover.error_ack'))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['carryovers', vehicleId, 'active'],
      })
      // Day-grid chip counts depend on the active-carryover set —
      // invalidate the week endpoint so the chip drops the moment the
      // mechanic acknowledges from the dialog.
      queryClient.invalidateQueries({ queryKey: ['carryover-counts'] })
      onAck()
    },
  })

  // Inline edit — body PATCH increments edit_count server-side. Stored
  // by-row so multiple rows can be in edit mode independently if Emil
  // ever wants to revise two notes at once.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState<string>('')
  const [editError, setEditError] = useState<string | null>(null)
  const editMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: string }) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/carryover/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ body }),
      })
      if (!res.ok) throw new Error(t('detail.carryover.edit_error'))
    },
    onSuccess: () => {
      setEditingId(null)
      setEditText('')
      setEditError(null)
      queryClient.invalidateQueries({
        queryKey: ['carryovers', vehicleId, 'active'],
      })
    },
    onError: (err) => {
      setEditError(
        err instanceof Error ? err.message : t('detail.carryover.edit_error'),
      )
    },
  })

  // Snooze — only valid when the banner is opened from a specific
  // appointment (we need an APPT id to snooze past). Hidden otherwise.
  const snoozeMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!sourceAppointmentId) return
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/carryover/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          snoozed_past_appointment_id: sourceAppointmentId,
        }),
      })
      if (!res.ok) throw new Error(t('detail.carryover.snooze_error'))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['carryovers', vehicleId, 'active'],
      })
      queryClient.invalidateQueries({ queryKey: ['carryover-counts'] })
    },
  })

  // Mechanic-side create path. Eric can also flag carryovers over SMS via
  // flag_carryover_note — that path doesn't touch this form; it POSTs the
  // same endpoint server-side via the brain. Either way the row shows up
  // in this banner the next time the appointment dialog opens.
  const flagMutation = useMutation({
    mutationFn: async (body: string) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/vehicles/${vehicleId}/carryover`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          body,
          source_appointment_id: sourceAppointmentId ?? undefined,
        }),
      })
      if (!res.ok) throw new Error(t('detail.carryover.flag_error'))
    },
    onSuccess: () => {
      setFlagText('')
      setFlagOpen(false)
      setFlagError(null)
      queryClient.invalidateQueries({
        queryKey: ['carryovers', vehicleId, 'active'],
      })
    },
    onError: (err) => {
      setFlagError(
        err instanceof Error ? err.message : t('detail.carryover.flag_error'),
      )
    },
  })

  const rows = carryoversQuery.data?.carryovers ?? []
  const hasRows = rows.length > 0

  // Nothing pending AND the create form is closed → render only the
  // affordance. Empty banner area is suppressed per the spec.
  if (!hasRows && !flagOpen) {
    return (
      <div className="-mt-1">
        <button
          type="button"
          onClick={() => {
            setFlagOpen(true)
            setFlagError(null)
          }}
          className="text-xs font-semibold text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
        >
          {t('detail.carryover.flag_button')}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {hasRows ? (
        <div className="rounded-[10px] border border-attention-line bg-attention-soft px-3 py-2 text-sm text-foreground">
          <div className="mb-1.5 text-[12px] font-semibold text-attention-text">
            {t('detail.carryover.header_count', { count: rows.length })}
          </div>
          <ul className="space-y-2">
            {rows.map((c) => {
              const isEric = c.flagged_by_eric
              // Assistant-flagged rows carry the saved lane (§2 assistant
              // attribution); human-flagged rows stay in the amber
              // waiting-on-you frame. Body text is ink either way (§1.2).
              const wrapperClass = isEric
                ? 'rounded-[10px] border border-saved-line bg-saved-soft px-2.5 py-2'
                : 'rounded-[10px] border border-attention-line bg-background/70 px-2.5 py-2'
              const labelClass = isEric ? 'text-saved-deep' : 'text-attention-text'
              const bodyClass = 'text-foreground'
              const metaClass = isEric ? 'text-saved-deep' : 'text-attention-text'
              // Prefer the source appointment's actual starts_at — "From
              // your last visit on May 14, 2026" is the visit date the
              // mechanic remembers. Falls back to flagged_at when the
              // carryover wasn't tied to an appointment (Eric flagged
              // from SMS without context).
              const dateLabel = c.source_appointment_starts_at
                ? t('detail.carryover.flagged_on_visit_of', {
                    date: formatDate(c.source_appointment_starts_at),
                  })
                : t('detail.carryover.from_visit_on', {
                    date: formatDate(c.flagged_at),
                  })
              const isEditing = editingId === c.id
              const canSnooze = !!sourceAppointmentId
              return (
                <li key={c.id} className={wrapperClass}>
                  <p
                    className={`mb-1 text-[12px] font-semibold ${labelClass}`}
                  >
                    {dateLabel}
                  </p>
                  {isEditing ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        className="min-h-[72px] text-sm"
                        autoFocus
                      />
                      {editError ? (
                        <p className="text-[11px] text-destructive">{editError}</p>
                      ) : null}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() =>
                            editMutation.mutate({
                              id: c.id,
                              body: editText.trim(),
                            })
                          }
                          disabled={
                            !editText.trim() ||
                            editText.trim() === c.body ||
                            editMutation.isPending
                          }
                        >
                          {editMutation.isPending
                            ? t('detail.carryover.edit_saving')
                            : t('detail.carryover.edit_save')}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingId(null)
                            setEditText('')
                            setEditError(null)
                          }}
                        >
                          {t('detail.carryover.edit_cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p
                        className={`whitespace-pre-wrap break-words ${bodyClass}`}
                      >
                        {c.body}
                      </p>
                      <p className={`mt-0.5 text-[11px] ${metaClass}`}>
                        {t(
                          isEric
                            ? 'detail.carryover.flagged_by_eric'
                            : 'detail.carryover.flagged_by_human',
                          { when: formatRelative(c.flagged_at) },
                        )}
                        {c.edit_count > 0 ? (
                          <>
                            {' · '}
                            {t('detail.carryover.edit_count', {
                              count: c.edit_count,
                            })}
                          </>
                        ) : null}
                      </p>
                      {/* Action footer — sits BELOW the body so the
                          note text uses the full card width on the
                          narrow AppointmentDetailDialog. Right-aligned
                          so the primary action (Acknowledge) lands at
                          the far edge of the card. */}
                      <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(c.id)
                            setEditText(c.body)
                            setEditError(null)
                          }}
                          className="text-xs"
                        >
                          {t('detail.carryover.edit')}
                        </Button>
                        {canSnooze ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => snoozeMutation.mutate(c.id)}
                            disabled={snoozeMutation.isPending}
                            className="text-xs"
                            title={
                              t('detail.carryover.snooze_help') ?? undefined
                            }
                          >
                            {snoozeMutation.isPending &&
                            snoozeMutation.variables === c.id
                              ? t('detail.carryover.snoozing')
                              : t('detail.carryover.snooze')}
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => ackMutation.mutate(c.id)}
                          disabled={ackMutation.isPending}
                        >
                          {ackMutation.isPending &&
                          ackMutation.variables === c.id
                            ? t('detail.carryover.acknowledging')
                            : t('detail.carryover.acknowledge')}
                        </Button>
                      </div>
                    </>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}

      {flagOpen ? (
        <div className="rounded-[10px] border border-dashed border-attention-line bg-attention-soft/60 px-3 py-2.5">
          <div className="mb-1.5 text-[12px] font-semibold text-attention-text">
            {t('detail.carryover.flag_header')}
          </div>
          <Textarea
            value={flagText}
            onChange={(e) => setFlagText(e.target.value)}
            placeholder={t('detail.carryover.flag_placeholder') ?? ''}
            className="min-h-[72px] text-sm"
            autoFocus
          />
          <p className="mt-1 text-[11px] text-ink-3">
            {t('detail.carryover.flag_help')}
          </p>
          {flagError ? (
            <p className="mt-1 text-[11px] text-destructive">{flagError}</p>
          ) : null}
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => flagMutation.mutate(flagText.trim())}
              disabled={!flagText.trim() || flagMutation.isPending}
            >
              {flagMutation.isPending
                ? t('detail.carryover.flag_submitting')
                : t('detail.carryover.flag_submit')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setFlagOpen(false)
                setFlagText('')
                setFlagError(null)
              }}
            >
              {t('detail.carryover.flag_cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <button
            type="button"
            onClick={() => {
              setFlagOpen(true)
              setFlagError(null)
            }}
            className="text-xs font-semibold text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
          >
            {t('detail.carryover.flag_button')}
          </button>
        </div>
      )}
    </div>
  )
}
